import PropTypes from 'prop-types';
import React from 'react';
import AutomationIcon from './AutomationIcon';
import Badge from '../../shared/components/Badge';
import Callout from '../../shared/components/Callout';
import { ACTION_COPY, PAGE_COPY, signalSummary } from '../constants/automationRules';

// The single place an outcome is decided, and the card that fixes what the old page got
// wrong: no detector above authorises anything, this does.
//
// DISPLAY ONLY. It writes nothing, because while unattended refunds are unavailable the one
// reachable option is already what happens to every match. The refund option still renders,
// because the two-outcome model is the thing this card exists to teach.
//
// Deliberately a plain list and NOT role="radiogroup"/role="radio". A radio that is focusable
// and announced as selectable, but carries no handler, tells a keyboard or screen-reader user
// they can choose something they cannot. Making this a real control is phase-2 work and needs
// a writer for the action setting (see FinanceAutomationRule::AUTO_REFUND_AVAILABLE) — it is
// not a matter of flipping the constant.
const SignalActionCard = ({
  enabledSignalCount,
  totalSignalCount,
  deferred,
  autoRefundAvailable,
  signalAction,
  onReviewFlagged,
}) => {
  // Which outcome is live comes from the server (teams.fraud_automation_signal_action, resolved
  // through FinanceAutomationRule.signal_action), so the card shows what the money path would
  // actually do rather than assuming "flag".
  const options = [
    { ...ACTION_COPY.flag, active: signalAction === 'flag', comingSoon: false },
    {
      ...ACTION_COPY.autoRefund,
      active: signalAction === 'auto_refund',
      comingSoon: !autoRefundAvailable,
    },
  ];

  return (
    <div className="automation-rules__card" role="group" aria-labelledby="action-card-title">
      <div className="automation-rules__card-head">
        <span className="automation-rules__card-icon" aria-hidden="true">
          <AutomationIcon name={ACTION_COPY.icon} />
        </span>
        <div className="automation-rules__card-main">
          <span className="automation-rules__card-title" id="action-card-title">
            {ACTION_COPY.title}
          </span>
          <p className="automation-rules__card-description">{ACTION_COPY.description}</p>
        </div>
      </div>

      <ul
        className="automation-rules__options"
        aria-label={ACTION_COPY.groupLabel}
        data-testid="signal-action-group"
      >
        {options.map((option) => (
          <li
            key={option.value}
            data-testid={`signal-action-${option.value}`}
            className={`automation-rules__option${
              option.active ? ' automation-rules__option--selected' : ''
            }`}
          >
            <span className="automation-rules__option-titles">
              <span className="automation-rules__option-marker" aria-hidden="true" />
              <span className="automation-rules__option-title">{option.title}</span>
              {/* Names the live outcome, since there is no checked state to announce. */}
              {option.active ? <span className="sr-only">{ACTION_COPY.activeLabel}</span> : null}
              {option.comingSoon ? <Badge label={PAGE_COPY.comingSoon} tone="neutral" /> : null}
            </span>
            <span className="automation-rules__option-body">{option.body}</span>
          </li>
        ))}
      </ul>

      <Callout tone="info" eyebrow={ACTION_COPY.summaryEyebrow} testId="signal-action-summary">
        {signalSummary(enabledSignalCount, totalSignalCount, deferred)}
        <button type="button" className="od-callout__link" onClick={onReviewFlagged}>
          {ACTION_COPY.summaryLink}
        </button>
      </Callout>
    </div>
  );
};

SignalActionCard.propTypes = {
  enabledSignalCount: PropTypes.number.isRequired,
  totalSignalCount: PropTypes.number.isRequired,
  deferred: PropTypes.bool,
  autoRefundAvailable: PropTypes.bool,
  signalAction: PropTypes.oneOf(['flag', 'auto_refund']),
  onReviewFlagged: PropTypes.func.isRequired,
};

SignalActionCard.defaultProps = {
  deferred: false,
  autoRefundAvailable: false,
  signalAction: 'flag',
};

export default SignalActionCard;
